import { useState } from 'react';
import { Search } from 'lucide-react';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { currency, decimal, type CurrencyCode } from '@/shared/lib/format';
import type { Passbook } from '@/shared/types';
import type { BenefitLineDef, OperatorProgram } from '../model/operator-rules';
import type { CatalogProduct } from '../model/operator-payload';
import { OperatorBenefitRow } from './operator-benefit-row';
import { OperatorMoneyField } from './operator-money-field';
import { OperatorCatalogPicker } from './operator-catalog-picker';
import { catalogProductLabel, isCatalogProductEnabled } from './operator-catalog-label';

interface OperatorBenefitsStepProps {
  benefitLines: BenefitLineDef[];
  benefitState: Record<string, boolean>;
  onBenefitToggle: (id: string, on: boolean) => void;
  redemptionAmount: string;
  onRedemptionAmountChange: (v: string) => void;
  currencyCode: CurrencyCode;
  program: OperatorProgram;
  passbook?: Passbook;
  /** Saldo total do cliente (hint Disponível vs Total no cashback). */
  balance?: number;
  catalogProducts?: CatalogProduct[];
  /** Query do catálogo ainda em voo — mantém a linha visível. */
  catalogLoading?: boolean;
  selectedProduct?: CatalogProduct | null;
  onSelectProduct?: (p: CatalogProduct | null) => void;
  /** Mensagem de erro quando o resgate > disponível. */
  redemptionError?: string | null;
}

export function OperatorBenefitsStep({
  benefitLines,
  benefitState,
  onBenefitToggle,
  redemptionAmount,
  onRedemptionAmountChange,
  currencyCode,
  program,
  passbook,
  balance = 0,
  catalogProducts,
  catalogLoading = false,
  selectedProduct,
  onSelectProduct,
  redemptionError,
}: OperatorBenefitsStepProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const visibleLines = benefitLines.filter((line) => {
    if (line.id !== 'catalog') return true;
    if (catalogLoading) return true;
    const products = (catalogProducts ?? []).filter(isCatalogProductEnabled);
    return products.length > 0;
  });

  if (visibleLines.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Benefícios</p>
        <h3 className="text-lg font-semibold tracking-tight">Nenhum benefício nesta conta</h3>
        <p className="text-sm text-muted-foreground">
          Você pode seguir e confirmar só o lançamento da compra.
        </p>
      </div>
    );
  }

  const eligibleProducts = (catalogProducts ?? []).filter(isCatalogProductEnabled);
  const catalogLine = visibleLines.find((l) => l.id === 'catalog');

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">Benefícios</p>
        <h3 className="text-lg font-semibold tracking-tight">Aplicar na conta</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Ative um resgate por vez — cashback/saldo ou produto do catálogo.
        </p>
      </div>

      <div className="space-y-2">
        {visibleLines.map((line) => (
          <OperatorBenefitRow
            key={line.id}
            line={line}
            enabled={Boolean(benefitState[line.id])}
            onToggle={(on) => onBenefitToggle(line.id, on)}
            detail={
              line.kind === 'cash' ? (
                <div className="space-y-1">
                  <OperatorMoneyField
                    label="Valor a resgatar"
                    value={redemptionAmount}
                    onChange={onRedemptionAmountChange}
                    currencyCode={currencyCode}
                    size="md"
                    invalid={Boolean(redemptionError && benefitState[line.id])}
                    hint={
                      line.maxAmount != null
                        ? balance > line.maxAmount + 1e-9
                          ? `Disponível: ${currency(line.maxAmount, currencyCode)} · Total: ${currency(balance, currencyCode)}`
                          : `Disponível: ${currency(line.maxAmount, currencyCode)}`
                        : undefined
                    }
                  />
                  {redemptionError && benefitState[line.id] ? (
                    <p className="text-sm font-medium text-destructive">{redemptionError}</p>
                  ) : null}
                </div>
              ) : line.kind === 'points' ? (
                <div className="space-y-1">
                  <Label className="text-xs">Pontos a resgatar</Label>
                  <Input
                    value={redemptionAmount}
                    onChange={(e) => onRedemptionAmountChange(e.target.value.replace(/\D/g, ''))}
                    placeholder="0"
                    className={`h-11 rounded-2xl ${redemptionError && benefitState[line.id] ? 'border-destructive' : ''}`}
                    inputMode="numeric"
                  />
                  {line.maxAmount != null ? (
                    <p className="text-xs text-muted-foreground">
                      Disponível: {decimal(line.maxAmount)}
                    </p>
                  ) : null}
                  {redemptionError && benefitState[line.id] ? (
                    <p className="text-sm font-medium text-destructive">{redemptionError}</p>
                  ) : null}
                </div>
              ) : line.kind === 'stamps' ? (
                redemptionError && benefitState[line.id] ? (
                  <p className="text-sm font-medium text-destructive">{redemptionError}</p>
                ) : line.maxAmount != null ? (
                  <p className="text-xs text-muted-foreground">
                    Resgata {line.maxAmount} selos da cartela
                  </p>
                ) : null
              ) : line.kind === 'product' ? (
                <div className="space-y-1">
                  {selectedProduct ? (
                    <div className="flex items-center justify-between gap-2 rounded-xl bg-primary/5 px-3 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {catalogProductLabel(selectedProduct, currencyCode, program, passbook)}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 shrink-0 px-2 text-xs"
                        onClick={() => onSelectProduct?.(null)}
                      >
                        Remover
                      </Button>
                    </div>
                  ) : null}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-10 w-full gap-1.5"
                    onClick={() => setPickerOpen(true)}
                    disabled={catalogLoading || eligibleProducts.length === 0}
                  >
                    <Search className="size-4" />
                    {selectedProduct ? 'Trocar produto' : 'Buscar no catálogo'}
                  </Button>
                  {redemptionError && benefitState[line.id] ? (
                    <p className="text-sm font-medium text-destructive">{redemptionError}</p>
                  ) : null}
                </div>
              ) : null
            }
          />
        ))}
      </div>

      <OperatorCatalogPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        products={catalogProducts ?? []}
        loading={catalogLoading}
        selectedProduct={selectedProduct}
        onSelectProduct={(p) => onSelectProduct?.(p)}
        currencyCode={currencyCode}
        program={program}
        passbook={passbook}
        title={catalogLine?.label ?? 'Resgatar produto do catálogo'}
      />
    </div>
  );
}
